import { useCallback } from 'react'
import { createLowerBoundPrices, createUpperBoundPrices } from '~/features/search/criteria'
import type { PriceCriteria } from '~/features/search/criteria'
import { toPriceLabel } from '~/features/search/criteria/price'
import { useNewPriceRange } from '~/lib/featureFlags/useNewPriceRange'
import { css } from '~/styled-system/css'
import type { UseApplyPrice } from '../priceSelect'
import { PriceRange } from '../priceSelect'
import { FilterChipsWithPopover, useClose, useDraft, useSetDraft } from './Popover'
import type { FilterCriteria } from './types'

type UseInitialPriceRange = () => PriceCriteria | undefined

type Props = {
  useFilterCriteria: () => FilterCriteria | undefined
  useInitialPriceRange: UseInitialPriceRange
  useApplyPrice: UseApplyPrice
}

export function Price({ useFilterCriteria, useApplyPrice, useInitialPriceRange }: Props) {
  const criteria = useFilterCriteria()

  const isNewPriceRange = useNewPriceRange()
  if (!isNewPriceRange) {
    return null
  }

  const data: PriceCriteria = {
    lowerBoundPrice: criteria?.lowerBoundPrice,
    upperBoundPrice: criteria?.upperBoundPrice,
  }
  const label = toPriceLabel(data)

  return (
    <FilterChipsWithPopover
      trigger={<span>{label === undefined ? '予算' : label}</span>}
      data={data}
      color={label === undefined ? 'default' : 'active'}
    >
      <Body useApplyPrice={useApplyPrice} useInitialPriceRange={useInitialPriceRange} />
    </FilterChipsWithPopover>
  )
}

function Body({
  useApplyPrice,
  useInitialPriceRange,
}: { useApplyPrice: UseApplyPrice; useInitialPriceRange: UseInitialPriceRange }) {
  const draft = useDraft<PriceCriteria | undefined>()
  const set = useSetDraft<PriceCriteria>()
  const initial = useInitialPriceRange()
  const useSetLowerBoundPrice = useCallback(() => {
    return (value: string) => {
      set((current: any) => ({
        ...current,
        lowerBoundPrice: value === '' ? undefined : Number(value),
      }))
    }
  }, [set])
  const useSetUpperBoundPrice = useCallback(() => {
    return (value: string) => {
      set((current: any) => ({
        ...current,
        upperBoundPrice: value === '' ? undefined : Number(value),
      }))
    }
  }, [set])
  const useLowerBoundPrice = useCallback(() => {
    return draft?.lowerBoundPrice?.toString() ?? ''
  }, [draft])
  const useUpperBoundPrice = useCallback(() => {
    return draft?.upperBoundPrice?.toString() ?? ''
  }, [draft])
  const useLowerBoundPriceRanges = useCallback(() => {
    const upper = draft?.upperBoundPrice ?? 0
    const init = initial?.upperBoundPrice ?? 0
    return createLowerBoundPrices(init < upper || upper === 0 ? init : upper)
  }, [draft, initial])
  const useUpperBoundPriceRanges = useCallback(() => {
    const lower = draft?.lowerBoundPrice ?? 0
    const init = initial?.lowerBoundPrice ?? 0
    return createUpperBoundPrices(lower < init ? init : lower)
  }, [draft, initial])
  return (
    <div
      className={css({
        width: '[288px]',
        borderRadius: 'md',
        backgroundColor: 'white',
        padding: '4',
        boxShadow: 'outline.lg',
      })}
    >
      <PriceRange
        lowerBound={{
          usePriceRanges: useLowerBoundPriceRanges,
          usePrice: useLowerBoundPrice,
          useSetPrice: useSetLowerBoundPrice,
        }}
        upperBound={{
          usePriceRanges: useUpperBoundPriceRanges,
          usePrice: useUpperBoundPrice,
          useSetPrice: useSetUpperBoundPrice,
        }}
        useApplyPrice={useApplyPrice}
        useClose={useClose}
        useDraft={useDraft}
      />
    </div>
  )
}
